import { useEffect, useState } from "react";
import { RainOverlay } from "@/components/Chart";
import { DataSourceInfo, TimeseriesResponse, StationStatisticsResponse } from "@/endpoints/rain/rain";
import { rainApi } from "@/services/rainApi";
import { StructureTimePeriodsValue } from "@/config/date";

export const RAIN_AGG_CONFIG: Record<StructureTimePeriodsValue, { rollingWindowMinutes: number; unit: string } | null> =
	{
		last24h: null,
		last7d: { rollingWindowMinutes: 60, unit: "mm/h" },
		last14d: { rollingWindowMinutes: 360, unit: "mm/6h" },
		last30d: { rollingWindowMinutes: 360, unit: "mm/6h" },
		last90d: { rollingWindowMinutes: 1440, unit: "mm/day" },
		last1y: { rollingWindowMinutes: 1440, unit: "mm/day" },
	};

export interface RainData {
	rainOverlay: RainOverlay | null;
	sourceInfo: DataSourceInfo | null;
}

const EMPTY_RAIN: RainData = { rainOverlay: null, sourceInfo: null };

export function useRainData(
	latitude: number,
	longitude: number,
	from: string,
	to: string,
	timePeriod: StructureTimePeriodsValue,
	aggOverride?: { rollingWindowMinutes: number; unit: string } | null,
): RainData {
	const [data, setData] = useState<RainData>(EMPTY_RAIN);

	useEffect(() => {
		if (!from || !to) return;

		const controller = new AbortController();
		const aggConfig = aggOverride !== undefined ? aggOverride : RAIN_AGG_CONFIG[timePeriod];

		const fetchRain = async () => {
			try {
				if (aggConfig) {
					const res = await rainApi.get<StationStatisticsResponse>("/rain/statistics", {
						params: {
							lon: longitude,
							lat: latitude,
							start: from,
							end: to,
							rollingWindowMinutes: aggConfig.rollingWindowMinutes,
							rollingAggregation: "sum",
						},
						signal: controller.signal,
					});
					if (controller.signal.aborted) return;
					const points: [number, number][] = (res.rollingWindows ?? []).map(w => [
						new Date(w.ts).getTime(),
						w.value,
					]);
					setData({
						rainOverlay: {
							data: points,
							unit: aggConfig.unit,
							pointRangeMs: aggConfig.rollingWindowMinutes * 60_000,
						},
						sourceInfo: res.sourceInfo,
					});
				} else {
					const res = await rainApi.get<TimeseriesResponse>("/rain/timeseries", {
						params: {
							lon: longitude,
							lat: latitude,
							start: from,
							end: to,
							samples: 500,
							gapFill: true,
						},
						signal: controller.signal,
					});
					if (controller.signal.aborted) return;
					const points: [number, number][] = res.data.map(d => [new Date(d.ts).getTime(), d.value]);
					let pointRangeMs: number | undefined;
					if (points.length >= 2) {
						const spanMs = points[points.length - 1][0] - points[0][0];
						pointRangeMs = spanMs / (points.length - 1);
					}
					setData({
						rainOverlay: { data: points, unit: "mm/h", pointRangeMs },
						sourceInfo: res.sourceInfo,
					});
				}
			} catch {
				if (!controller.signal.aborted) {
					setData(EMPTY_RAIN);
				}
			}
		};

		fetchRain();
		return () => controller.abort();
	}, [latitude, longitude, from, to, timePeriod, aggOverride]);

	return data;
}
